/**
 * Hit testing and snapping on flat point arrays ([x0, y0, x1, y1, …] in image px).
 * Ported from the original `features/editor/hitTest.ts` with the Reanimated `'worklet'`
 * directives removed. `snapPoint` implements the FIXED nearest-endpoint rule; the original
 * first-match rule is `snapPointOriginal` (also exported from `@better-lights/core/original`).
 */

export type RunPoints = number[][];

export interface View {
  s: number;
  tx: number;
  ty: number;
}

function screenDist(x: number, y: number, sx: number, sy: number, v: View): number {
  return Math.hypot(x * v.s + v.tx - sx, y * v.s + v.ty - sy);
}

/** Nearest vertex within `r` screen px: of the focused run, or of any run when `anyRun`. */
export function hitVertex(runs: RunPoints, focus: number, anyRun: boolean, sx: number, sy: number, v: View, r: number): [number, number] | null {
  let best: [number, number] | null = null;
  let bestD = r;
  for (let ri = 0; ri < runs.length; ri++) {
    if (focus >= 0 ? ri !== focus : !anyRun) continue;
    const pts = runs[ri];
    for (let i = 0; i < pts.length; i += 2) {
      const d = screenDist(pts[i], pts[i + 1], sx, sy, v);
      if (d <= bestD) {
        bestD = d;
        best = [ri, i / 2];
      }
    }
  }
  return best;
}

/** Midpoint handle of the focused (non-drawing) run: returns the insert index and point. */
export function hitMidpoint(runs: RunPoints, focus: number, drawing: boolean, sx: number, sy: number, v: View, r: number): [number, number, number, number] | null {
  if (focus < 0 || drawing) return null;
  const pts = runs[focus];
  if (!pts) return null;
  for (let i = 2; i < pts.length; i += 2) {
    const mx = (pts[i - 2] + pts[i]) / 2;
    const my = (pts[i - 1] + pts[i + 1]) / 2;
    if (screenDist(mx, my, sx, sy, v) <= r) return [focus, i / 2, mx, my];
  }
  return null;
}

/** Index of the run whose line passes within `r` screen px, or -1. */
export function hitRun(runs: RunPoints, sx: number, sy: number, v: View, r: number): number {
  const px = (sx - v.tx) / v.s;
  const py = (sy - v.ty) / v.s;
  const limit = r / v.s;
  let best = -1;
  let bestD = limit;
  for (let ri = 0; ri < runs.length; ri++) {
    const pts = runs[ri];
    for (let i = 2; i < pts.length; i += 2) {
      const ax = pts[i - 2];
      const ay = pts[i - 1];
      const dx = pts[i] - ax;
      const dy = pts[i + 1] - ay;
      const len2 = dx * dx + dy * dy;
      const t = len2 < 1e-9 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
      const d = Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
      if (d < bestD) {
        bestD = d;
        best = ri;
      }
    }
  }
  return best;
}

export type SnapFn = (
  runs: RunPoints,
  exceptRun: number,
  x: number,
  y: number,
  ax: number | null,
  ay: number | null,
  v: View,
  r: number,
) => [number, number, boolean];

/**
 * Snaps an image point to the NEAREST endpoint (first or last vertex) of another run within r
 * screen px, so corners join exactly; otherwise to horizontal / vertical / 45° from `anchor`
 * when within 3°. Fixed behavior (known issue 07 §B5): the original took the first endpoint in
 * project order (`snapPointOriginal`). Ties keep the earlier candidate. A run still cannot snap
 * onto itself (`exceptRun`).
 */
export const snapPoint: SnapFn = (runs, exceptRun, x, y, ax, ay, v, r) => {
  const sx = x * v.s + v.tx;
  const sy = y * v.s + v.ty;
  let best: [number, number] | null = null;
  let bestD = r;
  for (let ri = 0; ri < runs.length; ri++) {
    if (ri === exceptRun) continue;
    const pts = runs[ri];
    if (pts.length < 2) continue;
    for (const i of pts.length === 2 ? [0] : [0, pts.length - 2]) {
      const d = screenDist(pts[i], pts[i + 1], sx, sy, v);
      if (d <= bestD && (best === null || d < bestD)) {
        bestD = d;
        best = [pts[i], pts[i + 1]];
      }
    }
  }
  if (best) return [best[0], best[1], true];
  return angleSnap(x, y, ax, ay);
};

/**
 * ORIGINAL behavior (verbatim from the original app): snaps to the FIRST endpoint of another run
 * within r in project order (not the nearest), otherwise H/V/45° from the anchor.
 */
export const snapPointOriginal: SnapFn = (runs, exceptRun, x, y, ax, ay, v, r) => {
  const sx = x * v.s + v.tx;
  const sy = y * v.s + v.ty;
  for (let ri = 0; ri < runs.length; ri++) {
    if (ri === exceptRun) continue;
    const pts = runs[ri];
    if (pts.length < 2) continue;
    const ends = [0, pts.length - 2];
    for (let e = 0; e < ends.length; e++) {
      const i = ends[e];
      if (screenDist(pts[i], pts[i + 1], sx, sy, v) <= r) return [pts[i], pts[i + 1], true];
    }
  }
  return angleSnap(x, y, ax, ay);
};

/** H/V/45° snap of (x, y) relative to the anchor when within 3° (keeps the length). */
function angleSnap(x: number, y: number, ax: number | null, ay: number | null): [number, number, boolean] {
  if (ax === null || ay === null) return [x, y, false];
  const dx = x - ax;
  const dy = y - ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return [x, y, false];
  const angle = Math.atan2(dy, dx);
  const step = Math.PI / 4;
  const snapped = Math.round(angle / step) * step;
  if (Math.abs(angle - snapped) > (3 * Math.PI) / 180) return [x, y, false];
  return [ax + Math.cos(snapped) * len, ay + Math.sin(snapped) * len, true];
}

export function clampToImage(x: number, y: number, w: number, h: number): [number, number] {
  return [Math.max(0, Math.min(w, x)), Math.max(0, Math.min(h, y))];
}

/** Accepts taps slightly outside the photo (clamped), rejects ones far outside. */
export function nearImage(x: number, y: number, w: number, h: number): boolean {
  return x >= -w * 0.03 && x <= w * 1.03 && y >= -h * 0.03 && y <= h * 1.03;
}
